import React, { useState, useEffect, useRef } from 'react';
import { ProductPreview } from 'react-product-configurator';
import 'react-product-configurator/dist/index.css';
import ReactJson from 'react-json-view';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': any;
    }
  }
}

interface Item {
  name: string;
  price?: number;
  image: string;
  thumbnail?: string;
  color?: string;
  texture?: string;
  default?: boolean;
  layer?: number;
  showImageOnFrontend?: boolean;
}

interface Category {
  name: string;
  thumbnail: string;
  layer?: number;
  items: Item[];
}

interface CategoryGroup {
  id: string;
  name: string;
  categories: Category[];
}

interface Product {
  id: string;
  name: string;
  basePrice?: number;
  categories?: Category[];
  categoryGroupId?: string;
  previewType?: '2d' | '3d';
  model3dUrl?: string;
  hasCustomModel?: boolean;
  galleryImages?: string[];
}

// Available default images helper to select from
const DEFAULT_IMAGES = [
  '/img/thumbnail-case.png',
  '/img/case/case-red.png',
  '/img/case/case-blue.png',
  '/img/case/case-green.png',
  '/img/belt/belt-default.png',
  '/img/belt/belt-red.png',
  '/img/belt/belt-blue.png',
  '/img/belt/belt-green.png',
  '/img/handle/handle-default.png',
  '/img/handle/handle-red.png',
  '/img/handle/handle-blue.png',
  '/img/handle/handle-green.png'
];

// IndexedDB Helper Functions
const DB_NAME = "Configurator3DDB";
const STORE_NAME = "models";

function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveFile(key: string, file: File): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(file, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function getFile(key: string): Promise<File | null> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

async function deleteFile(key: string): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

const DEMO_FABRICS = [
  { name: 'None (Solid Color)', url: '' },
  { name: 'Local Texture (texture-1.jpg)', url: '/img/texture-1.jpg' },
  { name: 'Blue Denim', url: 'https://images.unsplash.com/photo-1541099649105-f69ad21f3246?w=600&auto=format&fit=crop' },
  { name: 'Beige Canvas', url: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?w=600&auto=format&fit=crop' },
  { name: 'Brown Leather', url: 'https://images.unsplash.com/photo-1590674899484-d5640e854abe?w=600&auto=format&fit=crop' },
  { name: 'Carbon Fiber', url: 'https://images.unsplash.com/photo-1508962914676-134849a727f0?w=600&auto=format&fit=crop' }
];

function App() {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([]);
  const [selectedCategoryGroupId, setSelectedCategoryGroupId] = useState<string>('');
  const [adminEditMode, setAdminEditMode] = useState<'product' | 'categoryGroup'>('product');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const [zoomImage, setZoomImage] = useState<{ url: string; title: string } | null>(null);
  const [gltfMaterials, setGltfMaterials] = useState<any[]>([]);

  // NOTE: We do NOT load window.THREE externally because it conflicts with model-viewer's
  // internal bundled Three.js instance and causes WebGL "useProgram: program not valid" errors.
  // All texture operations must go through model-viewer's own API.
  const [threeLoaded] = useState<boolean>(true); // Always ready since we use model-viewer API directly


  // Load and apply a texture using model-viewer's own internal API.
  // IMPORTANT: We must NOT use window.THREE or THREE.TextureLoader here because model-viewer
  // bundles its own internal Three.js instance. Mixing textures between two Three.js instances
  // causes WebGL "useProgram: program not valid" errors.
  /*
  const loadAndApplyTexture = async (modelViewer: any, mesh: any, textureUrl: string, callback?: () => void) => {
    console.log("[loadAndApplyTexture] Started with URL:", textureUrl);

    if (!modelViewer || !modelViewer.model || !modelViewer.model.materials) {
      console.warn("[loadAndApplyTexture] Aborted: modelViewer not ready.");
      if (callback) callback();
      return;
    }

    // Fix Unsplash CORS
    let processedUrl = textureUrl;
    if (processedUrl.includes('plus.unsplash.com')) {
      processedUrl = processedUrl.replace('plus.unsplash.com', 'images.unsplash.com');
    }

    // Find the model-viewer material wrapper for this mesh
    const materialName = mesh && mesh.material && !Array.isArray(mesh.material) ? mesh.material.name : '';
    const meshes = getMeshes(modelViewer);
    const meshIdx = meshes.findIndex((m: any) => m === mesh);
    console.log("[loadAndApplyTexture] mesh index:", meshIdx, "material name:", materialName);

    let mvMaterial = materialName
      ? modelViewer.model.materials.find((m: any) => m.name === materialName)
      : undefined;
    if (!mvMaterial && meshIdx !== -1 && modelViewer.model.materials[meshIdx]) {
      mvMaterial = modelViewer.model.materials[meshIdx];
    }
    if (!mvMaterial && modelViewer.model.materials.length > 0) {
      mvMaterial = modelViewer.model.materials[0];
    }

    if (!mvMaterial) {
      console.error("[loadAndApplyTexture] No matching model-viewer material found.");
      if (callback) callback();
      return;
    }

    console.log("[loadAndApplyTexture] Using mvMaterial:", mvMaterial.name, "| Calling createTexture...");

    try {
      // createTexture uses model-viewer's own internal Three.js — no WebGL conflict
      const texture = await modelViewer.createTexture(processedUrl);
      console.log("[loadAndApplyTexture] createTexture done. texture:", !!texture);

      if (!texture) {
        console.error("[loadAndApplyTexture] createTexture returned null for:", processedUrl);
        if (callback) callback();
        return;
      }

      // Apply via model-viewer's glTF material API (this updates the ORIGINAL material, not the clone)
      await mvMaterial.pbrMetallicRoughness.baseColorTexture.setTexture(texture);
      console.log("[loadAndApplyTexture] setTexture done on mvMaterial (original).");
      
      // Ensure material is non‑metallic for proper texture visibility
      await mvMaterial.pbrMetallicRoughness.setMetallicFactor(0);
      await mvMaterial.pbrMetallicRoughness.setRoughnessFactor(1);
      console.log('[loadAndApplyTexture] Set metallic=0, roughness=1 on mvMaterial');

      // Set baseColorFactor to white [1,1,1,1] so the texture shows fully without tinting
      await mvMaterial.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 1]);
      console.log("[loadAndApplyTexture] baseColorFactor set to white.");

      // CRITICAL: model-viewer's setTexture updates the ORIGINAL material's .map property,
      // but the mesh is rendering with a CLONED material. We copy the texture map AND all
      // PBR-critical properties from the original to the clone so Three.js renders correctly
      const copyTextureToClone = () => {
        const originalMat = mesh.material && mesh.material._originalMat ? mesh.material._originalMat : null;
        if (originalMat && originalMat.map) {
          // Directly use the original material that model-viewer updated
          mesh.material = originalMat;
          // Ensure renderer picks up changes
          if (mesh.material.needsUpdate !== undefined) mesh.material.needsUpdate = true;
          // Trigger model-viewer to re-render with the updated material
          if (typeof modelViewer.requestUpdate === 'function') modelViewer.requestUpdate();
          return true;
        }
        // Fallback – try cloning as before (unlikely needed)
        const applyToClone = (clonedMat: any) => {
          const orig = clonedMat._originalMat;
          if (orig && orig.map) {
            clonedMat.map = orig.map.clone();
            clonedMat.map.needsUpdate = true;
            clonedMat.metalness = 0;
            clonedMat.roughness = 1;
            clonedMat.envMap = null;
            clonedMat.envMapIntensity = 0;
            if (clonedMat.color) clonedMat.color.set(0xffffff);
            clonedMat.needsUpdate = true;
            console.log('[loadAndApplyTexture] Copied texture to clone as fallback');
            return true;
          }
          return false;
        };
        // Apply fallback to all materials in mesh
        let copied = false;
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((m: any) => { if (applyToClone(m)) copied = true; });
        } else {
          copied = applyToClone(mesh.material);
        }
        return copied;
      };


      // model-viewer may update the original material asynchronously — poll until .map is populated
      let attempts = 0;
      const pollAndCopy = () => {
        if (copyTextureToClone()) {
          forceViewerRender(modelViewer);
          if (typeof modelViewer.requestUpdate === 'function') modelViewer.requestUpdate();
          console.log("[loadAndApplyTexture] Done — texture visible on mesh.");
          if (callback) callback();
        } else if (attempts < 20) {
          attempts++;
          console.log("[loadAndApplyTexture] original.map not ready yet, retry", attempts);
          setTimeout(pollAndCopy, 100);
        } else {
          console.warn("[loadAndApplyTexture] Gave up waiting for original.map after 20 retries.");
          if (callback) callback();
        }
      };
      pollAndCopy();

      return; // callback is called inside pollAndCopy
    } catch (err) {
      console.error("[loadAndApplyTexture] Failed:", processedUrl, err);
    }

    if (callback) callback();
  };
  */


  
  // Custom router state
  const [currentPath, setCurrentPath] = useState<string>(window.location.pathname);
  const isAdminPath = currentPath === '/admin' || currentPath === '/admin/';
  const isTestPath = currentPath === '/test' || currentPath === '/test/';

  // Toggle light-theme class on body based on the route path
  useEffect(() => {
    if (isAdminPath) {
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
    }
  }, [isAdminPath]);

  // Editor selections
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(0);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragEnabledIndex, setDragEnabledIndex] = useState<number | null>(null);
  
  // Active dynamic customizer selections
  const [currentSelection, setCurrentSelection] = useState<Record<string, Item>>({});
  const [model3dUrlState, setModel3dUrlState] = useState<string>('');
  const [activeGalleryView, setActiveGalleryView] = useState<{ type: '2d' | '3d' | 'static'; url?: string }>({ type: '2d' });
  
  // Checkout & Order details
  const [bought, setBought] = useState<boolean>(false);
  const [boughtItem, setBoughtItem] = useState<any>({});
  const [quantity, setQuantity] = useState<number>(1);

  const modelViewerRef = useRef<any>(null);

  // 1. Dynamic script loader for Google's model-viewer
  useEffect(() => {
    if (!document.querySelector('script[src*="model-viewer"]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
      document.head.appendChild(script);
    }
  }, []);

  // 2. Sync with browser back/forward buttons
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // Helper to change routes
  const navigateTo = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };

  // Drag and drop handlers for reordering variants
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, hoverIndex: number) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === hoverIndex) return;
    if (dragOverIndex !== hoverIndex) {
      setDragOverIndex(hoverIndex);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const activeGroup = categoryGroups.find(g => g.id === selectedCategoryGroupId);
    if (draggedItemIndex === null || draggedItemIndex === dropIndex || !activeGroup) return;

    const category = activeGroup.categories[selectedCategoryIndex];
    const items = [...category.items];
    
    // Reorder items
    const draggedItem = items[draggedItemIndex];
    items.splice(draggedItemIndex, 1);
    items.splice(dropIndex, 0, draggedItem);

    const updatedCategories = activeGroup.categories.map((c, i) => 
      i === selectedCategoryIndex ? { ...c, items } : c
    );
    
    // Update active category group
    updateActiveCategoryGroup({ ...activeGroup, categories: updatedCategories });
    
    // Clean up drag states
    setDraggedItemIndex(null);
    setDragOverIndex(null);
    setDragEnabledIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
    setDragOverIndex(null);
    setDragEnabledIndex(null);
  };

  // 3. Load products & groups from LocalStorage on mount
  useEffect(() => {
    const localDb = localStorage.getItem('product_configurator_db');
    const localGroups = localStorage.getItem('product_configurator_groups');
    
    let parsedProducts: Product[] = [];
    let parsedGroups: CategoryGroup[] = [];
    
    if (localDb) {
      try {
        parsedProducts = JSON.parse(localDb);
      } catch (err) {
        console.error('Failed to parse local db', err);
      }
    }
    
    if (localGroups) {
      try {
        parsedGroups = JSON.parse(localGroups);
      } catch (err) {
        console.error('Failed to parse local groups', err);
      }
    }
    
    const isOutdated = parsedProducts.some((p: any) => p.id === 'backpack' && !p.hasCustomModel && (p.model3dUrl !== '/chair.glb'));
    
    if (parsedProducts.length === 0 || isOutdated) {
      fetchDefaultProducts();
    } else {
      // If we have products but no groups, migrate them!
      let dbChanged = false;
      if (parsedGroups.length === 0) {
        parsedProducts.forEach(prod => {
          if (prod.categories && prod.categories.length > 0 && !prod.categoryGroupId) {
            const newGroupId = prod.id + '_group';
            parsedGroups.push({
              id: newGroupId,
              name: prod.name + ' Categories',
              categories: prod.categories
            });
            prod.categoryGroupId = newGroupId;
            // Optionally delete the nested categories to clean up schema
            delete prod.categories;
          }
        });
        dbChanged = true;
      }
      
      // Ensure basePrice exists for all products
      parsedProducts.forEach(prod => {
        if (prod.basePrice === undefined) {
          prod.basePrice = prod.id === 'backpack' ? 49.99 : 0;
          dbChanged = true;
        }
      });

      if (dbChanged) {
        localStorage.setItem('product_configurator_groups', JSON.stringify(parsedGroups));
        localStorage.setItem('product_configurator_db', JSON.stringify(parsedProducts));
      }

      setProducts(parsedProducts);
      setCategoryGroups(parsedGroups);
      if (parsedProducts.length > 0) {
        setSelectedProductId(parsedProducts[0].id);
      }
      if (parsedGroups.length > 0) {
        setSelectedCategoryGroupId(parsedGroups[0].id || parsedGroups[0].id);
      }
    }
  }, []);

  const fetchDefaultProducts = () => {
    fetch('/products.json')
      .then(res => res.json())
      .then(data => {
        const defaultGroups: CategoryGroup[] = [];
        const migratedProducts = data.map((prod: any) => {
          let basePrice = prod.basePrice;
          if (basePrice === undefined) {
            basePrice = prod.id === 'backpack' ? 49.99 : 0;
          }
          if (prod.categories && prod.categories.length > 0 && !prod.categoryGroupId) {
            const newGroupId = prod.id + '_group';
            defaultGroups.push({
              id: newGroupId,
              name: prod.name + ' Categories',
              categories: prod.categories
            });
            const { categories, ...rest } = prod;
            return { ...rest, basePrice, categoryGroupId: newGroupId };
          }
          return { ...prod, basePrice };
        });
        
        setProducts(migratedProducts);
        setCategoryGroups(defaultGroups);
        if (migratedProducts.length > 0) {
          setSelectedProductId(migratedProducts[0].id);
        }
        if (defaultGroups.length > 0) {
          setSelectedCategoryGroupId(defaultGroups[0].id);
        }
        localStorage.setItem('product_configurator_db', JSON.stringify(migratedProducts));
        localStorage.setItem('product_configurator_groups', JSON.stringify(defaultGroups));
      })
      .catch(err => {
        console.error('Failed to fetch default products', err);
      });
  };

  // Find active product
  const activeProduct = products.find(p => p.id === selectedProductId);
  // Find category group assigned to active product, fallback to product's own categories (for backward compatibility)
  const activeCategoryGroup = categoryGroups.find(g => g.id === activeProduct?.categoryGroupId);
  const activeCategories = activeCategoryGroup ? activeCategoryGroup.categories : (activeProduct?.categories || []);

  // 4. Update customizer selections and preview mode when active product changes
  useEffect(() => {
    if (!activeProduct) return;
    const initialSelection: Record<string, Item> = {};
    activeCategories.forEach(cat => {
      const defaultItem = cat.items.find(item => item.default) || cat.items[0];
      if (defaultItem) {
        initialSelection[cat.name] = defaultItem;
      }
    });
    setCurrentSelection(initialSelection);
    setActiveGalleryView({ type: activeProduct.previewType || '2d' });
    setBought(false);
    setGltfMaterials([]);
  }, [selectedProductId, products, categoryGroups]);

  // 5. Fetch GLB file from IndexedDB and update model3dUrlState when selected product changes
  useEffect(() => {
    if (!selectedProductId) return;
    const active = products.find(p => p.id === selectedProductId);
    
    // Revoke previous URL to avoid memory leaks
    if (model3dUrlState.startsWith('blob:')) {
      URL.revokeObjectURL(model3dUrlState);
    }
    
    if (active && active.hasCustomModel) {
      getFile(selectedProductId).then(file => {
        if (file) {
          const url = URL.createObjectURL(file);
          setModel3dUrlState(url);
        } else {
          setModel3dUrlState('');
        }
      });
    } else {
      setModel3dUrlState(active?.model3dUrl || '');
    }
  }, [selectedProductId, products]);

  // Helper to extract meshes from the underlying Three.js scene of <model-viewer>
  const getMeshes = (modelViewer: any) => {
    if (!modelViewer) return [];
    const sceneSym = Object.getOwnPropertySymbols(modelViewer).find(
      (s) => s.description === 'scene'
    );
    const scene = sceneSym ? modelViewer[sceneSym] : null;
    if (!scene || !scene.model) return [];

    const meshes: any[] = [];
    scene.model.traverse((child: any) => {
      if (child.isMesh) {
        // Clone material so changes to this mesh don't affect others if they shared it originally.
        // IMPORTANT: Store reference to the original material (_originalMat) on the clone so we
        // can copy textures from it later (model-viewer's API updates the original, not the clone).
        if (child.material) {
          if (Array.isArray(child.material)) {
            if (child.material.length > 0 && !child.material[0]._cloned) {
              child.material = child.material.map((mat: any) => {
                const c = mat.clone();
                c._cloned = true;
                c._originalMat = mat; // keep reference to original
                return c;
              });
            }
          } else {
            if (!child.material._cloned) {
              const originalMat = child.material;
              child.material = child.material.clone();
              child.material._cloned = true;
              child.material._originalMat = originalMat; // keep reference to original
            }
          }
        }
        meshes.push(child);
      }
    });
    return meshes;
  };

  const forceViewerRender = (modelViewer: any) => {
    if (!modelViewer) return;
    if (typeof modelViewer.requestUpdate === 'function') {
      modelViewer.requestUpdate();
    }
    try {
      const sceneSym = Object.getOwnPropertySymbols(modelViewer).find(
        (s) => s.description === 'scene'
      );
      const scene = sceneSym ? modelViewer[sceneSym] : null;
      if (scene && typeof scene.queueRender === 'function') {
        scene.queueRender();
      }
    } catch (e) {
      console.warn("Failed to call queueRender", e);
    }
  };

  // Helper to synchronize customizer colors with 3D model materials
  const sync3DColors = () => {
    const modelViewer = modelViewerRef.current;
    if (modelViewer && activeProduct && modelViewer.model && modelViewer.model.materials) {
      const meshes = getMeshes(modelViewer);
      meshes.forEach((mesh: any, idx: number) => {
        const meshName = mesh.name ? mesh.name : 'layer_' + idx;
        // Find matching category by name
        let matchingCategory = mesh.name ? activeCategories.find((cat: any) =>
          mesh.name.toLowerCase().includes(cat.name.toLowerCase()) ||
          cat.name.toLowerCase().includes(mesh.name.toLowerCase())
        ) : undefined;

        // Fallback by index if not found
        if (!matchingCategory && activeCategories.length > 0) {
          matchingCategory = activeCategories[idx % activeCategories.length];
        }

        if (matchingCategory) {
          // Check selection specifically for this layer name, fallback to category name
          const selectedOption = currentSelection[meshName] || currentSelection[matchingCategory.name];
          if (selectedOption) {
            const materialName = mesh.material && !Array.isArray(mesh.material) ? mesh.material.name : '';
            // Find corresponding model-viewer material wrapper to use the official API
            let mvMaterial = materialName ? modelViewer.model.materials.find((m: any) => m.name === materialName) : undefined;
            if (!mvMaterial && modelViewer.model.materials[idx]) {
              mvMaterial = modelViewer.model.materials[idx];
            }

            // if (selectedOption.texture) {
            //   loadAndApplyTexture(modelViewer, mesh, selectedOption.texture);
            // } else 
            if (selectedOption.color) {
              const colorStr = selectedOption.color;
              // Clear texture using model-viewer API
              if (mvMaterial) {
                try {
                  mvMaterial.pbrMetallicRoughness.baseColorTexture.setTexture(null);
                  
                  // Convert color code to 0-1 rgb factor
                  const hex = colorStr.replace('#', '');
                  const r = parseInt(hex.substring(0, 2), 16) / 255;
                  const g = parseInt(hex.substring(2, 4), 16) / 255;
                  const b = parseInt(hex.substring(4, 6), 16) / 255;
                  mvMaterial.pbrMetallicRoughness.setBaseColorFactor([r, g, b, 1.0]);
                } catch (e) {
                  console.error("Error setting color via model-viewer API:", e);
                }
              }
              // Also clear Three.js texture just in case
              if (mesh.material) {
                if (Array.isArray(mesh.material)) {
                  mesh.material.forEach((mat: any) => {
                    mat.map = null;
                    if (mat.color) mat.color.set(colorStr);
                    mat.needsUpdate = true;
                  });
                } else {
                  mesh.material.map = null;
                  if (mesh.material.color) mesh.material.color.set(colorStr);
                  mesh.material.needsUpdate = true;
                }
              }
            }
          }
        }
      });
      // Force repaint so direct Three.js modifications show up immediately without interaction
      forceViewerRender(modelViewer);
    }
  };

  // Sync colors when selections, model url, or Three.js loading state changes
  useEffect(() => {
    if (threeLoaded) {
      sync3DColors();
    }
  }, [currentSelection, activeGalleryView.type, model3dUrlState, threeLoaded]);

  const handleModelLoad = () => {
    const modelViewer = modelViewerRef.current;
    if (modelViewer) {
      // Small timeout to ensure the scene has finished assembling children
      setTimeout(() => {
        const meshes = getMeshes(modelViewer);
        setGltfMaterials(meshes);

        // Populate currentSelection for materials that do not have them yet
        const updatedSelection = { ...currentSelection };
        let changed = false;

        meshes.forEach((mesh: any, idx: number) => {
          const meshName = mesh.name ? mesh.name : 'layer_' + idx;
          if (!updatedSelection[meshName]) {
            // Find matching category
            let matchingCategory = mesh.name ? activeCategories.find((cat: any) =>
              mesh.name.toLowerCase().includes(cat.name.toLowerCase()) ||
              cat.name.toLowerCase().includes(mesh.name.toLowerCase())
            ) : undefined;

            if (!matchingCategory && activeCategories.length > 0) {
              matchingCategory = activeCategories[idx % activeCategories.length];
            }

            if (matchingCategory) {
              const defaultItem = matchingCategory.items.find((item: any) => item.default) || matchingCategory.items[0];
              if (defaultItem) {
                updatedSelection[meshName] = defaultItem;
                changed = true;
              }
            }
          }
        });

        if (changed) {
          setCurrentSelection(updatedSelection);
        }
        sync3DColors();
      }, 100);
    }
  };

  // Sync colors immediately when model has finished loading in <model-viewer>
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (modelViewer) {
      modelViewer.addEventListener('load', handleModelLoad);
      // Fallback if model is already loaded
      if (modelViewer.model && modelViewer.model.materials) {
        handleModelLoad();
      }
      return () => {
        modelViewer.removeEventListener('load', handleModelLoad);
      };
    }
    return undefined;
  }, [model3dUrlState, activeGalleryView.type]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === 'admin' && password === '123456') {
      setIsAdmin(true);
      setLoginError('');
      setUsername('');
      setPassword('');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    navigateTo('/');
  };

  const handleReset = () => {
    if (window.confirm('Reset all configurations to defaults? All custom changes will be lost.')) {
      fetchDefaultProducts();
      setSelectedCategoryIndex(0);
      alert('Database reset to default!');
    }
  };

  const handleSave = () => {
    localStorage.setItem('product_configurator_db', JSON.stringify(products));
    localStorage.setItem('product_configurator_groups', JSON.stringify(categoryGroups));
    alert('Configurations saved successfully!');
  };

  const handleDownload = () => {
    const payload = { products, categoryGroups };
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(payload, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", "configurator_config.json");
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const updateActiveProduct = (updated: Product) => {
    const updatedProducts = products.map(p => p.id === selectedProductId ? updated : p);
    setProducts(updatedProducts);
  };

  const updateActiveCategoryGroup = (updated: CategoryGroup) => {
    const updatedGroups = categoryGroups.map(g => g.id === selectedCategoryGroupId ? updated : g);
    setCategoryGroups(updatedGroups);
  };

  // Upload GLB handler
  const handleGlbUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !activeProduct) return;
    const file = files[0];
    
    try {
      // Save file to IndexedDB mapped to selectedProductId
      await saveFile(selectedProductId, file);
      
      const updatedProduct: Product = {
        ...activeProduct,
        hasCustomModel: true,
        previewType: '3d',
        model3dUrl: '' // Reset direct URL string since we load from IndexedDB
      };
      
      const updatedProducts = products.map(p => p.id === selectedProductId ? updatedProduct : p);
      setProducts(updatedProducts);
      localStorage.setItem('product_configurator_db', JSON.stringify(updatedProducts));
      
      // Update local state so it immediately forces rendering
      const url = URL.createObjectURL(file);
      setModel3dUrlState(url);
      
      alert('3D model uploaded and applied successfully!');
    } catch (err) {
      console.error('Failed to save GLB file to IndexedDB', err);
      alert('Failed to upload GLB model file');
    }
  };

  const addProduct = () => {
    const name = prompt("Enter product name:");
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (products.some(p => p.id === id)) {
      alert("Product with this name or ID already exists!");
      return;
    }
    const newProduct: Product = {
      id,
      name,
      basePrice: 0,
      categoryGroupId: categoryGroups[0]?.id || '',
      previewType: '2d'
    };
    const updated = [...products, newProduct];
    setProducts(updated);
    setSelectedProductId(id);
    setAdminEditMode('product');
  };

  const deleteProduct = () => {
    if (!activeProduct) return;
    if (window.confirm(`Delete product "${activeProduct.name}"?`)) {
      const updated = products.filter(p => p.id !== selectedProductId);
      setProducts(updated);
      deleteFile(selectedProductId); // Clean up database
      if (updated.length > 0) {
        setSelectedProductId(updated[0].id);
      } else {
        setSelectedProductId('');
      }
      setAdminEditMode('product');
    }
  };

  const addCategoryGroup = () => {
    const name = prompt("Enter category group name (e.g. Strap / Accessories, Sofa Cushions):");
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (categoryGroups.some(g => g.id === id)) {
      alert("Category Group with this name already exists!");
      return;
    }
    const newGroup: CategoryGroup = {
      id,
      name,
      categories: []
    };
    setCategoryGroups([...categoryGroups, newGroup]);
    setSelectedCategoryGroupId(id);
    setSelectedCategoryIndex(0);
    setAdminEditMode('categoryGroup');
  };

  const deleteCategoryGroup = (groupId: string) => {
    const group = categoryGroups.find(g => g.id === groupId);
    if (!group) return;
    if (window.confirm(`Delete category group "${group.name}"? All nested categories and variants will be lost.`)) {
      const updated = categoryGroups.filter(g => g.id !== groupId);
      setCategoryGroups(updated);
      
      // Update any products using this group to empty
      const updatedProducts = products.map(p => p.categoryGroupId === groupId ? { ...p, categoryGroupId: '' } : p);
      setProducts(updatedProducts);
      
      if (updated.length > 0) {
        setSelectedCategoryGroupId(updated[0].id);
      } else {
        setSelectedCategoryGroupId('');
      }
      setSelectedCategoryIndex(0);
      setAdminEditMode('categoryGroup');
    }
  };

  const addCategory = () => {
    const activeGroup = categoryGroups.find(g => g.id === selectedCategoryGroupId);
    if (!activeGroup) {
      alert("Please select a category group first.");
      return;
    }
    const name = prompt("Enter category name (e.g. Strap, Base):");
    if (!name) return;
    if (activeGroup.categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      alert("Category already exists in this group!");
      return;
    }
    const newCategory: Category = {
      name,
      layer: activeGroup.categories.length,
      thumbnail: '/img/thumbnail-case.png',
      items: []
    };
    const updatedGroup = {
      ...activeGroup,
      categories: [...activeGroup.categories, newCategory]
    };
    updateActiveCategoryGroup(updatedGroup);
    setSelectedCategoryIndex(updatedGroup.categories.length - 1);
  };

  const deleteCategory = (index: number) => {
    const activeGroup = categoryGroups.find(g => g.id === selectedCategoryGroupId);
    if (!activeGroup) return;
    if (window.confirm(`Delete category "${activeGroup.categories[index].name}"?`)) {
      const updatedCats = activeGroup.categories.filter((_, i) => i !== index);
      const adjustedCats = updatedCats.map((c, i) => ({ ...c, layer: i }));
      const updatedGroup = {
        ...activeGroup,
        categories: adjustedCats
      };
      updateActiveCategoryGroup(updatedGroup);
      setSelectedCategoryIndex(0);
    }
  };

  const addItem = (catIndex: number) => {
    const activeGroup = categoryGroups.find(g => g.id === selectedCategoryGroupId);
    if (!activeGroup) return;
    const name = prompt("Enter variant name (e.g. Red, Matte Black):");
    if (!name) return;
    
    const category = activeGroup.categories[catIndex];
    const isFirstItem = category.items.length === 0;

    const newItem: Item = {
      name,
      price: 9.99,
      color: '#6366f1',
      image: '/img/thumbnail-case.png',
      default: isFirstItem
    };

    const updatedCategory = {
      ...category,
      items: [...category.items, newItem]
    };

    const updatedCategories = activeGroup.categories.map((c, i) => i === catIndex ? updatedCategory : c);
    updateActiveCategoryGroup({
      ...activeGroup,
      categories: updatedCategories
    });
  };

  const updateItem = (catIndex: number, itemIndex: number, key: keyof Item, value: any) => {
    const activeGroup = categoryGroups.find(g => g.id === selectedCategoryGroupId);
    if (!activeGroup) return;
    const category = activeGroup.categories[catIndex];
    
    let updatedItems = category.items.map((item, idx) => {
      if (idx === itemIndex) {
        return { ...item, [key]: value };
      }
      if (key === 'default' && value === true && idx !== itemIndex) {
        return { ...item, default: false };
      }
      return item;
    });

    const updatedCategories = activeGroup.categories.map((c, i) => i === catIndex ? { ...c, items: updatedItems } : c);
    updateActiveCategoryGroup({
      ...activeGroup,
      categories: updatedCategories
    });
  };

  const deleteItem = (catIndex: number, itemIndex: number) => {
    const activeGroup = categoryGroups.find(g => g.id === selectedCategoryGroupId);
    if (!activeGroup) return;
    const category = activeGroup.categories[catIndex];
    if (window.confirm(`Delete variant "${category.items[itemIndex].name}"?`)) {
      const updatedItems = category.items.filter((_, idx) => idx !== itemIndex);
      
      if (category.items[itemIndex].default && updatedItems.length > 0) {
        updatedItems[0].default = true;
      }

      const updatedCategories = activeGroup.categories.map((c, i) => i === catIndex ? { ...c, items: updatedItems } : c);
      updateActiveCategoryGroup({
        ...activeGroup,
        categories: updatedCategories
      });
    }
  };

  const onBuySubmit = () => {
    setBought(true);
    setBoughtItem({
      product: activeProduct?.name,
      quantity,
      price: calculatePrice() * quantity,
      selections: currentSelection
    });
  };

  const calculatePrice = (): number => {
    let price = activeProduct && typeof activeProduct.basePrice === 'number' ? activeProduct.basePrice : 0;
    Object.values(currentSelection).forEach(item => {
      if (item && item.price) {
        price += item.price;
      }
    });
    return parseFloat(price.toFixed(2));
  };

  // Collect all variant images that should be shown in the gallery sidebar
  const variantGalleryImages: { item: Item; categoryName: string }[] = [];
  activeCategories.forEach(cat => {
    cat.items.forEach(item => {
      if (item.showImageOnFrontend) {
        variantGalleryImages.push({ item, categoryName: cat.name });
      }
    });
  });

  return (
    <div>
      {/* Global Navbar */}
      <header className="app-header">
        <div className="brand" onClick={() => navigateTo('/')} style={{cursor: 'pointer'}}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{color: '#6366f1'}}>
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
          Configurator Suite
        </div>
        <div className="nav-controls">
          {products.length > 0 && !isAdminPath && !isTestPath && (
            <div className="flex-row" style={{marginRight: '1rem'}}>
              <span className="text-muted" style={{fontSize: '0.875rem'}}>Select Model:</span>
              <select 
                className="form-input" 
                value={selectedProductId}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  setSelectedCategoryIndex(0);
                  setBought(false);
                }}
                style={{padding: '0.35rem 0.75rem'}}
              >
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          <button 
            className={`btn ${isTestPath ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => navigateTo(isTestPath ? '/' : '/test')}
            style={{marginRight: '0.5rem'}}
          >
            {isTestPath ? 'View Store' : 'OBJ Viewer'}
          </button>

          {isAdmin ? (
            <>
              {isAdminPath ? (
                <button className="btn btn-primary" onClick={() => navigateTo('/')}>
                  View Store
                </button>
              ) : (
                <button className="btn btn-primary" onClick={() => navigateTo('/admin')}>
                  Admin Dashboard
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleLogout}>
                Logout
              </button>
            </>
          ) : (
            !isAdminPath && (
              <button className="btn btn-secondary" onClick={() => navigateTo('/admin')}>
                Admin Dashboard
              </button>
            )
          )}
        </div>
      </header>

      {/* Main View Router */}
      {isTestPath ? (
        <TestOBJRoute />
      ) : isAdminPath ? (
        isAdmin ? (
          /* Admin Editor Dashboard */
          <div className="admin-container">
            {/* Admin Sidebar */}
            <div className="admin-sidebar">
              {/* Global Actions */}
              <div>
                <div className="sidebar-title">Global Actions</div>
                <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                  <button className="btn btn-success" onClick={handleSave} style={{width: '100%', justifyContent: 'center'}}>
                    Save Database
                  </button>
                  <button className="btn btn-secondary" onClick={handleDownload} style={{width: '100%', justifyContent: 'center'}}>
                    Export JSON
                  </button>
                  <button className="btn btn-danger" onClick={handleReset} style={{width: '100%', justifyContent: 'center'}}>
                    Reset Defaults
                  </button>
                </div>
              </div>

              {/* Products Section */}
              <div>
                <div className="sidebar-title" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>Products</span>
                  <button className="btn btn-secondary" onClick={addProduct} style={{padding: '0.15rem 0.5rem', fontSize: '0.75rem'}}>+ Add</button>
                </div>
                <div className="list-items">
                  {products.map(p => (
                    <div 
                      key={p.id} 
                      className={`sidebar-item ${adminEditMode === 'product' && p.id === selectedProductId ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedProductId(p.id);
                        setAdminEditMode('product');
                        if (p.categoryGroupId) {
                          setSelectedCategoryGroupId(p.categoryGroupId);
                        }
                      }}
                    >
                      <span>{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Groups Section */}
              <div>
                <div className="sidebar-title" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>Category Groups</span>
                  <button className="btn btn-secondary" onClick={addCategoryGroup} style={{padding: '0.15rem 0.5rem', fontSize: '0.75rem'}}>+ Add</button>
                </div>
                <div className="list-items">
                  {categoryGroups.map(g => (
                    <div key={g.id}>
                      <div 
                        className={`sidebar-item ${adminEditMode === 'categoryGroup' && g.id === selectedCategoryGroupId ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedCategoryGroupId(g.id);
                          setAdminEditMode('categoryGroup');
                          setSelectedCategoryIndex(0);
                        }}
                        style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                      >
                        <span>{g.name}</span>
                        <span 
                          className="text-danger" 
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCategoryGroup(g.id);
                          }}
                          style={{fontSize: '0.8rem', padding: '0 0.25rem'}}
                          title="Delete Group"
                        >
                          ✕
                        </span>
                      </div>
                      
                      {/* Expanded Sub-Categories for Selected Group */}
                      {adminEditMode === 'categoryGroup' && g.id === selectedCategoryGroupId && (
                        <div style={{paddingLeft: '0.75rem', marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.2rem', borderLeft: '1px dashed var(--border-color)'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0.5rem'}}>
                            <span style={{fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)'}}>Categories</span>
                            <button className="btn btn-secondary" onClick={addCategory} style={{padding: '0.1rem 0.35rem', fontSize: '0.65rem'}}>+ Add</button>
                          </div>
                          {g.categories.map((cat, idx) => (
                            <div 
                              key={idx} 
                              className={`sidebar-item ${idx === selectedCategoryIndex ? 'active' : ''}`}
                              onClick={() => setSelectedCategoryIndex(idx)}
                              style={{padding: '0.35rem 0.5rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}
                            >
                              <img 
                                src={cat.thumbnail} 
                                alt={cat.name} 
                                style={{ width: '20px', height: '20px', borderRadius: '0.2rem', objectFit: 'contain', background: 'var(--bg-dark)' }} 
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://placehold.co/50x50?text=?';
                                }}
                              />
                              <span style={{ flex: 1 }}>{cat.name} (L{cat.layer})</span>
                              <span 
                                className="text-danger" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteCategory(idx);
                                }}
                                style={{fontSize: '0.75rem', padding: '0 0.15rem'}}
                              >
                                ✕
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Admin Content Pane */}
            <div className="admin-content">
              {adminEditMode === 'product' ? (
                !activeProduct ? (
                  <div style={{textAlign: 'center', padding: '4rem'}}>
                    <h2>No Product Selected</h2>
                    <p className="text-muted">Please add or select a product from the sidebar to begin editing.</p>
                  </div>
                ) : (
                  <div>
                    {/* Product Details Section */}
                    <div className="editor-card">
                      <div className="editor-header">
                        <h2 className="editor-title">Product Details</h2>
                        <button className="btn btn-danger" onClick={deleteProduct}>Delete Product</button>
                      </div>
                      
                      <div className="grid-3" style={{alignItems: 'end'}}>
                        <div className="form-group">
                          <label className="form-label">Product Name</label>
                          <input 
                            type="text" 
                            className="form-input" 
                            value={activeProduct.name}
                            onChange={(e) => updateActiveProduct({ ...activeProduct, name: e.target.value })}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Default Preview Mode</label>
                          <select 
                            className="form-input"
                            value={activeProduct.previewType || '2d'}
                            onChange={(e) => updateActiveProduct({ ...activeProduct, previewType: e.target.value as any })}
                          >
                            <option value="2d">2D Overlay (Layered Images)</option>
                            <option value="3d">3D Model Viewer (.glb)</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Upload 3D Model (.glb)</label>
                          <input 
                            type="file" 
                            accept=".glb"
                            className="form-input" 
                            onChange={handleGlbUpload}
                            style={{padding: '0.25rem 0.5rem'}}
                          />
                        </div>
                      </div>

                      <div className="grid-2" style={{marginTop: '1.25rem', alignItems: 'end'}}>
                        <div className="form-group">
                          <label className="form-label">Linked Category Group</label>
                          <select 
                            className="form-input"
                            value={activeProduct.categoryGroupId || ''}
                            onChange={(e) => {
                              updateActiveProduct({ ...activeProduct, categoryGroupId: e.target.value });
                              setSelectedCategoryGroupId(e.target.value);
                            }}
                          >
                            <option value="">-- None (No Customizations) --</option>
                            {categoryGroups.map(g => (
                              <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Base Price ($)</label>
                          <input 
                            type="number" 
                            step="0.01"
                            className="form-input" 
                            value={activeProduct.basePrice ?? 0}
                            onChange={(e) => updateActiveProduct({ ...activeProduct, basePrice: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>

                      {activeProduct.hasCustomModel && (
                        <div style={{marginTop: '1rem', color: '#10b981', fontSize: '0.875rem', fontWeight: 600}}>
                          ✓ Custom 3D Model (.glb) uploaded and active in local IndexedDB store.
                        </div>
                      )}
                    </div>
                  </div>
                )
              ) : (
                /* Category Group Details mode */
                (() => {
                  const activeGroup = categoryGroups.find(g => g.id === selectedCategoryGroupId);
                  if (!activeGroup) {
                    return (
                      <div style={{textAlign: 'center', padding: '4rem'}}>
                        <h2>No Category Group Selected</h2>
                        <p className="text-muted">Please add or select a category group from the sidebar to begin editing.</p>
                      </div>
                    );
                  }
                  return (
                    <div>
                      {/* Category Group Settings Card */}
                      <div className="editor-card">
                        <div className="editor-header">
                          <h2 className="editor-title">Category Group Details</h2>
                          <button className="btn btn-danger" onClick={() => deleteCategoryGroup(activeGroup.id)}>Delete Group</button>
                        </div>
                        
                        <div className="grid-2" style={{alignItems: 'end'}}>
                          <div className="form-group">
                            <label className="form-label">Group Name</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={activeGroup.name}
                              onChange={(e) => updateActiveCategoryGroup({ ...activeGroup, name: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {activeGroup.categories.length > 0 && activeGroup.categories[selectedCategoryIndex] ? (
                        (() => {
                          const currentCategory = activeGroup.categories[selectedCategoryIndex];
                          return (
                            <div className="editor-card">
                              <div className="editor-header">
                                <h2 className="editor-title">Category: {currentCategory.name}</h2>
                                <button className="btn btn-danger" onClick={() => deleteCategory(selectedCategoryIndex)}>
                                  Delete Category
                                </button>
                              </div>
                              
                              <div className="grid-3" style={{marginBottom: '1.5rem'}}>
                                <div className="form-group">
                                  <label className="form-label">Category Name</label>
                                  <input 
                                    type="text" 
                                    className="form-input" 
                                    value={currentCategory.name}
                                    onChange={(e) => {
                                      const updatedCats = activeGroup.categories.map((c, i) => 
                                        i === selectedCategoryIndex ? { ...c, name: e.target.value } : c
                                      );
                                      updateActiveCategoryGroup({ ...activeGroup, categories: updatedCats });
                                    }}
                                  />
                                </div>
                                
                                <div className="form-group">
                                  <label className="form-label">Layer Order (z-index)</label>
                                  <input 
                                    type="number" 
                                    className="form-input" 
                                    value={currentCategory.layer ?? 0}
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value) || 0;
                                      const updatedCats = activeGroup.categories.map((c, i) => 
                                        i === selectedCategoryIndex ? { ...c, layer: val } : c
                                      );
                                      updateActiveCategoryGroup({ ...activeGroup, categories: updatedCats });
                                    }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label className="form-label">Category Thumbnail</label>
                                  <div className="flex-row" style={{ gap: '0.75rem', alignItems: 'center' }}>
                                    <div 
                                      className="variant-preview" 
                                      onClick={() => setZoomImage({ url: currentCategory.thumbnail, title: currentCategory.name })} 
                                      title="Click to zoom image"
                                      style={{ cursor: 'pointer', flexShrink: 0 }}
                                    >
                                      <img 
                                        src={currentCategory.thumbnail} 
                                        alt={currentCategory.name} 
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=Missing';
                                        }}
                                      />
                                    </div>
                                    <select 
                                      className="form-input"
                                      value={currentCategory.thumbnail}
                                      onChange={(e) => {
                                        const updatedCats = activeGroup.categories.map((c, i) => 
                                          i === selectedCategoryIndex ? { ...c, thumbnail: e.target.value } : c
                                        );
                                        updateActiveCategoryGroup({ ...activeGroup, categories: updatedCats });
                                      }}
                                      style={{flex: 1}}
                                    >
                                      {DEFAULT_IMAGES.map(img => (
                                        <option key={img} value={img}>{img.split('/').pop()}</option>
                                      ))}
                                      <option value={currentCategory.thumbnail}>Custom...</option>
                                    </select>
                                    <input 
                                      type="text" 
                                      className="form-input" 
                                      value={currentCategory.thumbnail}
                                      onChange={(e) => {
                                        const updatedCats = activeGroup.categories.map((c, i) => 
                                          i === selectedCategoryIndex ? { ...c, thumbnail: e.target.value } : c
                                        );
                                        updateActiveCategoryGroup({ ...activeGroup, categories: updatedCats });
                                      }}
                                      style={{flex: 1}}
                                      placeholder="Custom image path"
                                    />
                                  </div>
                                </div>
                              </div>

                              <div style={{borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem'}}>
                                <div className="flex-between" style={{marginBottom: '1rem'}}>
                                  <h3 style={{margin: 0, fontSize: '1.1rem'}}>Variants / Items</h3>
                                  <button className="btn btn-primary" onClick={() => addItem(selectedCategoryIndex)}>
                                    + Add Variant
                                  </button>
                                </div>

                                {currentCategory.items.length === 0 ? (
                                  <div style={{textAlign: 'center', padding: '2rem', border: '1px dashed var(--border-color)', borderRadius: '0.5rem'}}>
                                    <p className="text-muted">No variants created for this category yet. Click Add Variant above.</p>
                                  </div>
                                ) : (
                                  <div className="variant-grid">
                                    {currentCategory.items.map((item, itemIdx) => (
                                      <div 
                                        key={itemIdx} 
                                        className={`variant-card ${draggedItemIndex === itemIdx ? 'dragging' : ''} ${dragOverIndex === itemIdx ? 'drag-hover' : ''}`}
                                        draggable={dragEnabledIndex === itemIdx}
                                        onDragStart={(e) => handleDragStart(e, itemIdx)}
                                        onDragOver={(e) => handleDragOver(e, itemIdx)}
                                        onDrop={(e) => handleDrop(e, itemIdx)}
                                        onDragEnd={handleDragEnd}
                                      >
                                        <div 
                                          className="drag-handle" 
                                          title="Drag to reorder variant"
                                          onMouseDown={() => setDragEnabledIndex(itemIdx)}
                                          onMouseUp={() => setDragEnabledIndex(null)}
                                          onMouseLeave={() => setDragEnabledIndex(null)}
                                        >
                                          ⋮⋮
                                        </div>
                                        <div className="variant-preview" onClick={() => setZoomImage({ url: item.image, title: item.name })} title="Click to zoom image">
                                          <img src={item.image} alt={item.name} onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=Missing';
                                          }}/>
                                        </div>
                                        
                                        <div className="variant-inputs">
                                          <div className="form-group" style={{marginBottom: 0}}>
                                            <label className="form-label">Variant Name</label>
                                            <input 
                                              type="text" 
                                              className="form-input" 
                                              value={item.name}
                                              onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'name', e.target.value)}
                                              style={{padding: '0.4rem 0.6rem'}}
                                            />
                                          </div>
                                          
                                          <div className="form-group" style={{marginBottom: 0}}>
                                            <label className="form-label">Price ($)</label>
                                            <input 
                                              type="number" 
                                              step="0.01"
                                              className="form-input" 
                                              value={item.price ?? 0}
                                              onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'price', parseFloat(e.target.value) || 0)}
                                              style={{padding: '0.4rem 0.6rem'}}
                                            />
                                          </div>

                                          <div className="form-group" style={{marginBottom: 0}}>
                                            <label className="form-label">Color Code</label>
                                            <div className="color-input-wrapper">
                                              <div className="color-picker-dot" style={{backgroundColor: item.color || '#fff'}}>
                                                <input 
                                                  type="color" 
                                                  value={item.color || '#ffffff'}
                                                  onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'color', e.target.value)}
                                                />
                                              </div>
                                              <input 
                                                type="text" 
                                                className="form-input" 
                                                value={item.color || ''}
                                                onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'color', e.target.value)}
                                                placeholder="#ffffff"
                                                style={{width: '80px', padding: '0.4rem 0.6rem'}}
                                              />
                                            </div>
                                          </div>

                                          <div className="form-group" style={{marginBottom: 0}}>
                                            <label className="form-label">Texture URL (Optional)</label>
                                            <div className="flex-row">
                                              <select
                                                className="form-input"
                                                value={DEMO_FABRICS.find(f => f.url === item.texture)?.url || (item.texture ? item.texture : '')}
                                                onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'texture', e.target.value)}
                                                style={{width: '120px', padding: '0.4rem 0.6rem'}}
                                              >
                                                <option value="">Solid Color</option>
                                                {DEMO_FABRICS.filter(f => f.url !== '').map(f => (
                                                  <option key={f.name} value={f.url}>{f.name}</option>
                                                ))}
                                                {item.texture && !DEMO_FABRICS.some(f => f.url === item.texture) && (
                                                  <option value={item.texture}>Custom Texture...</option>
                                                )}
                                              </select>
                                              <input 
                                                type="text" 
                                                className="form-input" 
                                                value={item.texture || ''}
                                                onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'texture', e.target.value)}
                                                placeholder="Texture image URL..."
                                                style={{flex: 1, padding: '0.4rem 0.6rem'}}
                                              />
                                            </div>
                                          </div>

                                          <div className="form-group" style={{marginBottom: 0}}>
                                            <label className="form-label">Image Path</label>
                                            <div className="flex-row">
                                              <select
                                                className="form-input"
                                                value={item.image}
                                                onChange={(e) => {
                                                  updateItem(selectedCategoryIndex, itemIdx, 'image', e.target.value);
                                                  updateItem(selectedCategoryIndex, itemIdx, 'thumbnail', e.target.value);
                                                }}
                                                style={{width: '100px', padding: '0.4rem 0.6rem'}}
                                              >
                                                {DEFAULT_IMAGES.map(img => (
                                                  <option key={img} value={img}>{img.split('/').pop()}</option>
                                                ))}
                                                <option value={item.image}>Custom...</option>
                                              </select>
                                              <input 
                                                type="text" 
                                                className="form-input" 
                                                value={item.image}
                                                onChange={(e) => {
                                                  updateItem(selectedCategoryIndex, itemIdx, 'image', e.target.value);
                                                  updateItem(selectedCategoryIndex, itemIdx, 'thumbnail', e.target.value);
                                                }}
                                                placeholder="/img/..."
                                                style={{flex: 1, padding: '0.4rem 0.6rem'}}
                                              />
                                            </div>
                                          </div>
                                        </div>

                                        <div className="flex-row" style={{gap: '1rem'}}>
                                          <label className="checkbox-label">
                                            <input 
                                              type="checkbox" 
                                              checked={item.default || false}
                                              onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'default', e.target.checked)}
                                            />
                                            Default
                                          </label>
                                          
                                          <label className="checkbox-label" title="Show variant thumbnail image in front-end swatches instead of solid color swatch">
                                            <input 
                                              type="checkbox" 
                                              checked={item.showImageOnFrontend || false}
                                              onChange={(e) => updateItem(selectedCategoryIndex, itemIdx, 'showImageOnFrontend', e.target.checked)}
                                            />
                                            Show Image
                                          </label>
                                          
                                          <button 
                                            className="btn btn-danger" 
                                            onClick={() => deleteItem(selectedCategoryIndex, itemIdx)}
                                            style={{padding: '0.4rem 0.8rem'}}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="editor-card" style={{textAlign: 'center', padding: '2rem'}}>
                          <p className="text-muted">No categories created yet for this group. Click "+ Add" next to Categories on the sidebar.</p>
                        </div>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        ) : (
          /* Centered Login Page for /admin when not authenticated */
          <div style={{display: 'flex', height: 'calc(100vh - 73px)', justifyContent: 'center', alignItems: 'center', background: '#0b0f19'}}>
            <div className="modal-content" style={{boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)', width: '380px'}}>
              <h2 className="modal-title" style={{marginBottom: '0.5rem'}}>Admin Login</h2>
              <p className="text-muted" style={{textAlign: 'center', fontSize: '0.875rem', marginBottom: '2rem'}}>Enter admin credentials to edit configs.</p>
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="admin"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input 
                    type="password" 
                    className="form-input" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    required
                  />
                </div>
                {loginError && <div className="error-text" style={{textAlign: 'center'}}>{loginError}</div>}
                <button type="submit" className="btn btn-primary" style={{width: '100%', justifyContent: 'center', marginTop: '1.5rem', padding: '0.75rem'}}>
                  Login
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigateTo('/')} style={{width: '100%', justifyContent: 'center', marginTop: '0.75rem', padding: '0.75rem'}}>
                  Back to Configurator
                </button>
              </form>
            </div>
          </div>
        )
      ) : (
        /* Configurator Mode (Path !== '/admin') */
        <div className="pdp-wrapper">
          {!activeProduct ? (
            <div style={{margin: 'auto', textAlign: 'center', padding: '2rem'}}>
              <h2>No Product Configuration Available</h2>
              <p className="text-muted">Please visit <button onClick={() => navigateTo('/admin')} style={{background: 'none', border: 'none', color: '#6366f1', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit'}}>Admin Dashboard</button> to create your first configurable product.</p>
            </div>
          ) : bought ? (
            /* Checkout Success Panel */
            <div style={{display: 'flex', height: '100%', width: '100%', padding: '3rem', boxSizing: 'border-box', gap: '2rem', gridColumn: 'span 2'}}>
              <div style={{ width: "60%", background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '1rem', padding: '2rem' }}>
                <h1 style={{marginTop: 0}}>Product Preview</h1>
                <p className="text-muted">Your customized build outside of the editor:</p>
                <div style={{height: '350px', background: 'var(--bg-dark)', borderRadius: '0.5rem', overflow: 'hidden'}}>
                  <ProductPreview currentSelection={boughtItem.selections} />
                </div>
                <button className="btn btn-primary" onClick={() => setBought(false)} style={{marginTop: '2rem'}}>
                  Customize Again
                </button>
              </div>
              <div style={{ width: "40%", background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '1rem', padding: '2rem', display: 'flex', flexDirection: 'column' }}>
                <h1 style={{marginTop: 0}}>Order Details (JSON)</h1>
                <div style={{flex: 1, overflowY: 'auto', background: 'var(--bg-dark)', borderRadius: '0.5rem', padding: '1rem'}}>
                  <ReactJson 
                    src={boughtItem} 
                    theme={"rhea" as any}
                    style={{backgroundColor: 'transparent'}}
                    displayDataTypes={false}
                    name={false}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Premium PDP E-Commerce Layout */
            <>
              {/* Left Column: Image gallery + interactive viewer */}
              <div className="pdp-left">
                <div className="pdp-left-inner">
                  {/* Thumbnail Selector Sidebar */}
                  <div className="pdp-gallery-sidebar">
                    {/* 2D Overlay Thumbnail */}
                    <div 
                      className={`gallery-thumb ${activeGalleryView.type === '2d' ? 'active' : ''}`}
                      onClick={() => setActiveGalleryView({ type: '2d' })}
                      title="Customizable 2D Overlay"
                    >
                      <span className="gallery-thumb-icon" role="img" aria-label="backpack">🎒</span>
                      <span className="gallery-thumb-label">2D Custom</span>
                    </div>

                    {/* 3D Model Thumbnail */}
                    {(activeProduct.hasCustomModel || activeProduct.model3dUrl) && (
                      <div 
                        className={`gallery-thumb ${activeGalleryView.type === '3d' ? 'active' : ''}`}
                        onClick={() => setActiveGalleryView({ type: '3d' })}
                        title="Interactive 3D Model"
                      >
                        <span className="gallery-thumb-icon" role="img" aria-label="cube">📦</span>
                        <span className="gallery-thumb-label">3D Orbit</span>
                      </div>
                    )}



                    {/* Dynamic Variant Gallery Thumbnails */}
                    {variantGalleryImages.map((variantObj, idx) => {
                      const imgUrl = variantObj.item.thumbnail || variantObj.item.image;
                      const isActive = activeGalleryView.type === 'static' && activeGalleryView.url === imgUrl;
                      return (
                        <div 
                          key={`variant-gallery-${idx}`}
                          className={`gallery-thumb ${isActive ? 'active' : ''}`}
                          onClick={() => setActiveGalleryView({ type: 'static', url: imgUrl })}
                          title={`${variantObj.categoryName}: ${variantObj.item.name}`}
                        >
                          <img src={imgUrl} alt={variantObj.item.name} onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=Image';
                          }} />
                          <span className="gallery-thumb-label">{variantObj.item.name}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Main Active Viewer Area */}
                  <div className="pdp-main-viewer">
                    {activeGalleryView.type === '3d' && model3dUrlState ? (
                      <model-viewer
                        ref={modelViewerRef}
                        src={model3dUrlState}
                        alt={`3D Model of ${activeProduct.name}`}
                        camera-controls
                        ar
                        shadow-intensity="1.5"
                        shadow-softness="1"
                        exposure="1.2"
                        environment-image="neutral"
                      ></model-viewer>
                    ) : activeGalleryView.type === '2d' ? (
                      <div className="canvas-container">
                        <ProductPreview currentSelection={currentSelection} />
                      </div>
                    ) : (
                      <img 
                        src={activeGalleryView.url} 
                        alt="Gallery Static View" 
                        className="static-image-viewer"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Image+Not+Found';
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column: Title, descriptions, customized category select */}
              <div className="pdp-right">
                <div className="pdp-title-section">
                  <div className="pdp-brand-text">Premium Customizer</div>
                  <h1 className="pdp-title">{activeProduct.name}</h1>
                  <div className="pdp-rating-row">
                    <span className="stars">★★★★★</span>
                    <span>4.9 (148 reviews)</span>
                  </div>
                </div>

                <div className="pdp-price-row">
                  <span className="pdp-price">${calculatePrice()}</span>
                  <span className="text-muted" style={{fontSize: '0.875rem', textDecoration: 'line-through'}}>${(calculatePrice() * 1.2).toFixed(2)}</span>
                </div>

                <p className="pdp-description">
                  Configure your handcrafted {activeProduct.name.toLowerCase()} in real-time. Choose from premium canvas tones, weather-resistant buckles, and ergonomic straps. Hand-finished by our master tailors.
                </p>

                {/* Categories configurations loop */}
                {(activeGalleryView.type === '3d' && gltfMaterials.length > 0 ? gltfMaterials : activeCategories).map((itemOrCategory: any, idx) => {
                  let name = '';
                  let matchingCategory: Category | undefined;
                  let selectedVal: Item | undefined;

                  if (activeGalleryView.type === '3d' && gltfMaterials.length > 0) {
                    name = itemOrCategory.name ? itemOrCategory.name : 'layer_' + idx;
                    matchingCategory = itemOrCategory.name ? activeCategories.find(cat => 
                      itemOrCategory.name.toLowerCase().includes(cat.name.toLowerCase()) ||
                      cat.name.toLowerCase().includes(itemOrCategory.name.toLowerCase())
                    ) : undefined;
                    if (!matchingCategory && activeCategories.length > 0) {
                      matchingCategory = activeCategories[idx % activeCategories.length];
                    }
                    if (matchingCategory) {
                      selectedVal = currentSelection[name] || currentSelection[matchingCategory.name];
                    }
                  } else {
                    name = itemOrCategory.name;
                    matchingCategory = itemOrCategory;
                    selectedVal = currentSelection[name];
                  }

                  if (!matchingCategory) return null;

                  const displayName = activeGalleryView.type === '3d' && gltfMaterials.length > 0
                    ? (itemOrCategory.name ? itemOrCategory.name.replace(/[_-]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'Layer ' + (idx + 1))
                    : matchingCategory.name;

                  return (
                    <div key={idx} className="pdp-config-section">
                      <div className="config-category-title">
                        <span>Configure {displayName}</span>
                        <span className="config-category-selected">{selectedVal ? selectedVal.name : 'Not selected'}</span>
                      </div>
                      <div className="options-list">
                        {matchingCategory.items.map((item, itemIdx) => {
                          const isActive = selectedVal && selectedVal.name === item.name;
                          return (
                            <div 
                              key={itemIdx} 
                              className={`option-item ${isActive ? 'active' : ''}`}
                              onClick={() => {
                                console.log("[swatch onClick] Swatch clicked:", { name, item, activeGalleryViewType: activeGalleryView.type });
                                const newSelection = { ...currentSelection };
                                if (activeGalleryView.type === '3d' && gltfMaterials.length > 0) {
                                  newSelection[name] = item;
                                } else {
                                  newSelection[matchingCategory!.name] = item;
                                }
                                setCurrentSelection(newSelection);

                                // Dynamically apply texture or color directly to mesh material
                                if (activeGalleryView.type === '3d') {
                                  // if (item.texture) {
                                  //   console.log("[swatch onClick] Selected option has texture. Calling loadAndApplyTexture...");
                                  //   loadAndApplyTexture(modelViewerRef.current, itemOrCategory, item.texture, () => {
                                  //     console.log("[swatch onClick] loadAndApplyTexture callback executed. Forcing render...");
                                  //     forceViewerRender(modelViewerRef.current);
                                  //   });
                                  // } else 
                                  if (item.color) {
                                    console.log("[swatch onClick] Selected option has color:", item.color);
                                    const colorStr = item.color;
                                    const materialName = itemOrCategory && itemOrCategory.material && !Array.isArray(itemOrCategory.material) ? itemOrCategory.material.name : '';
                                    let mvMaterial = materialName ? modelViewerRef.current?.model?.materials?.find((m: any) => m.name === materialName) : undefined;
                                    if (!mvMaterial && modelViewerRef.current?.model?.materials?.[idx]) {
                                      mvMaterial = modelViewerRef.current.model.materials[idx];
                                    }
                                    if (mvMaterial) {
                                      try {
                                        mvMaterial.pbrMetallicRoughness.baseColorTexture.setTexture(null);
                                        const hex = colorStr.replace('#', '');
                                        const r = parseInt(hex.substring(0, 2), 16) / 255;
                                        const g = parseInt(hex.substring(2, 4), 16) / 255;
                                        const b = parseInt(hex.substring(4, 6), 16) / 255;
                                        mvMaterial.pbrMetallicRoughness.setBaseColorFactor([r, g, b, 1.0]);
                                      } catch (e) {
                                        console.error("Error setting color via model-viewer API", e);
                                      }
                                    }
                                    if (itemOrCategory && itemOrCategory.material) {
                                      if (Array.isArray(itemOrCategory.material)) {
                                        itemOrCategory.material.forEach((mat: any) => {
                                          mat.map = null;
                                          if (mat.color) mat.color.set(colorStr);
                                          mat.needsUpdate = true;
                                        });
                                      } else {
                                        itemOrCategory.material.map = null;
                                        if (itemOrCategory.material.color) {
                                          itemOrCategory.material.color.set(colorStr);
                                        }
                                        itemOrCategory.material.needsUpdate = true;
                                      }
                                    }
                                    forceViewerRender(modelViewerRef.current);
                                  }
                                }
                              }}
                            >
                              {/* item.texture ? (
                                <div className="option-color-swatch" style={{backgroundImage: `url(${item.texture})`, backgroundSize: 'cover', backgroundPosition: 'center'}} />
                              ) : */}
                              {item.color ? (
                                <div className="option-color-swatch" style={{backgroundColor: item.color}} />
                              ) : (
                                <img src={item.thumbnail || item.image} alt={item.name} className="option-img-thumbnail" />
                              )}
                              <div className="option-item-tooltip">
                                {item.name} {item.price ? `(+$${item.price.toFixed(2)})` : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Features Highlights */}
                <div className="pdp-features-list">
                  <div className="pdp-feature-item">
                    <span role="img" aria-label="backpack">🎒</span> 25L Capacity
                  </div>
                  <div className="pdp-feature-item">
                    <span role="img" aria-label="laptop">💻</span> 16" Laptop Sleeve
                  </div>
                  <div className="pdp-feature-item">
                    <span role="img" aria-label="rain">🌧️</span> Weatherproof Zipper
                  </div>
                  <div className="pdp-feature-item">
                    <span role="img" aria-label="shield">🛡️</span> 5-Year Warranty
                  </div>
                </div>

                {/* Purchase buttons */}
                <div className="purchase-section">
                  <input 
                    type="number" 
                    className="qty-input" 
                    value={quantity} 
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                  />
                  <button 
                    className="btn btn-primary" 
                    onClick={onBuySubmit} 
                    style={{flex: 1, padding: '0.85rem', justifyContent: 'center', fontSize: '1rem'}}
                  >
                    Add Custom Build to Cart
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Lightbox Image Zoom Modal */}
      {zoomImage && (
        <div className="lightbox-overlay" onClick={() => setZoomImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setZoomImage(null)}>✕</button>
            <img src={zoomImage.url} alt={zoomImage.title} className="lightbox-img" onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Image+Not+Found';
            }}/>
            <div className="lightbox-title">{zoomImage.title}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function TestOBJRoute() {
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [layers, setLayers] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<any>(null);
  const sceneRef = useRef<any>(null);
  const cameraRef = useRef<any>(null);
  const controlsRef = useRef<any>(null);
  const currentModelRef = useRef<any>(null);

  // Dynamic script loader for Three.js and loaders
  useEffect(() => {
    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
      });
    };

    loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js')
      .then(() => {
        return Promise.all([
          loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js'),
          loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js')
        ]);
      })
      .then(() => {
        setScriptsLoaded(true);
      })
      .catch((err) => {
        setErrorMsg('Failed to load Three.js 3D rendering libraries.');
        console.error(err);
      });
  }, []);

  // Initialize Three.js scene
  useEffect(() => {
    if (!scriptsLoaded || !canvasContainerRef.current) return;

    const THREE = (window as any).THREE;
    const container = canvasContainerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight || 500;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf1f5f9); // Light slate bg
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 100000);
    camera.position.set(0, 0, 10);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controlsRef.current = controls;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight1.position.set(5, 10, 7);
    dirLight1.castShadow = true;
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight2.position.set(-5, -5, -5);
    scene.add(dirLight2);

    // Animation Loop
    let animationFrameId: number;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize Handler
    const handleResize = () => {
      if (!container || !rendererRef.current || !cameraRef.current) return;
      const w = container.clientWidth;
      const h = container.clientHeight || 500;
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      if (renderer && renderer.domElement) {
        container.removeChild(renderer.domElement);
        renderer.dispose();
      }
    };
  }, [scriptsLoaded]);

  // Handle file import
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMsg('');
    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      try {
        parseAndLoadGlb(arrayBuffer);
      } catch (err) {
        setErrorMsg('Error parsing the GLB file. Make sure it is a valid 3D .glb file.');
        console.error(err);
      }
    };
    reader.onerror = () => {
      setErrorMsg('Failed to read the file.');
    };
    reader.readAsArrayBuffer(file);
  };

  const parseAndLoadGlb = (arrayBuffer: ArrayBuffer) => {
    const THREE = (window as any).THREE;
    if (!THREE || !sceneRef.current) return;

    // Clear previous model
    if (currentModelRef.current) {
      sceneRef.current.remove(currentModelRef.current);
      currentModelRef.current = null;
    }

    const loader = new THREE.GLTFLoader();
    loader.parse(
      arrayBuffer,
      '',
      (gltf: any) => {
        const obj = gltf.scene;
        currentModelRef.current = obj;

        const detectedLayers: any[] = [];
        
        // Process meshes and materials
        obj.traverse((child: any) => {
          if (child.isMesh) {
            // Clone material so changes to this mesh don't affect others if they shared it originally
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material = child.material.map((mat: any) => mat.clone());
              } else {
                child.material = child.material.clone();
              }
            }
            
            // Give unique name if mesh has no name
            const layerName = child.name || `Mesh_${detectedLayers.length + 1}`;
            child.name = layerName;

            // Get starting color
            let initialColor = '#cccccc';
            const primaryMat = Array.isArray(child.material) ? child.material[0] : child.material;
            if (primaryMat && primaryMat.color) {
              initialColor = '#' + primaryMat.color.getHexString();
            }

            detectedLayers.push({
              name: layerName,
              uuid: child.uuid,
              mesh: child,
              color: initialColor
            });
          }
        });

        // Add to scene
        sceneRef.current.add(obj);

        // Compute bounding box to center and scale camera view
        const box = new THREE.Box3().setFromObject(obj);
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        box.getCenter(center);
        box.getSize(size);

        // Re-center object
        obj.position.sub(center);

        // Adjust camera positioning
        const maxDim = Math.max(size.x, size.y, size.z);
        
        // Dynamically adjust camera clipping planes based on model size to prevent z-axis clipping
        if (cameraRef.current) {
          cameraRef.current.near = Math.min(0.01, maxDim / 1000);
          cameraRef.current.far = Math.max(100000, maxDim * 20);
          cameraRef.current.updateProjectionMatrix();
        }

        const fov = cameraRef.current ? cameraRef.current.fov * (Math.PI / 180) : 45 * (Math.PI / 180);
        let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
        cameraZ *= 2.0; // Zoom out slightly more to ensure the entire model is inside the viewport
        
        if (cameraRef.current) {
          cameraRef.current.position.set(0, maxDim * 0.15, cameraZ);
          cameraRef.current.lookAt(0, 0, 0);
        }

        if (controlsRef.current) {
          controlsRef.current.target.set(0, 0, 0);
          controlsRef.current.maxDistance = cameraZ * 10;
          controlsRef.current.minDistance = maxDim * 0.05;
          controlsRef.current.update();
        }

        setLayers(detectedLayers);
        setModelLoaded(true);
      },
      (error: any) => {
        setErrorMsg('Failed to parse the GLB file. Make sure it is a valid binary GLTF (.glb) model.');
        console.error(error);
      }
    );
  };

  const handleColorChange = (uuid: string, newColor: string) => {
    const THREE = (window as any).THREE;
    const targetLayer = layers.find(l => l.uuid === uuid);
    if (!targetLayer || !targetLayer.mesh) return;

    const mesh = targetLayer.mesh;
    const colorVal = new THREE.Color(newColor);

    if (mesh.material) {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((mat: any) => {
          if (mat.color) mat.color.copy(colorVal);
        });
      } else {
        if (mesh.material.color) {
          mesh.material.color.copy(colorVal);
        }
      }
    }

    setLayers(prev => prev.map(l => l.uuid === uuid ? { ...l, color: newColor } : l));
  };

  return (
    <div className="test-viewer-container">
      {/* 3D Viewer Area */}
      <div className="test-viewer-3d">
        <div ref={canvasContainerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
          {!scriptsLoaded && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <div className="spinner" />
              <div style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Loading 3D Engine...</div>
            </div>
          )}
          {scriptsLoaded && !modelLoaded && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/>
              </svg>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-color)' }}>No 3D Model Loaded</h3>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Please select a 3D .glb file from the sidebar to inspect its layers and customize materials.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Control Sidebar Area */}
      <div className="test-viewer-sidebar">
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 0.25rem 0', fontSize: '1.25rem', fontWeight: 700 }}>GLB Layer Customizer</h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Upload a GLB file, inspect its meshes (layers), and pick colors in real-time.
          </p>
        </div>

        <div className="editor-card" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontWeight: 600 }}>Select .glb File</label>
            <input 
              type="file" 
              accept=".glb" 
              className="form-input" 
              onChange={handleFileChange}
              style={{ padding: '0.5rem' }}
            />
          </div>
          {errorMsg && <div className="error-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>{errorMsg}</div>}
        </div>

        {modelLoaded && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
              Detected Layers / Meshes ({layers.length})
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', overflowY: 'auto', flex: 1, paddingRight: '0.25rem' }}>
              {layers.map((layer) => (
                <div key={layer.uuid} className="layer-card">
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <span className="layer-name" title={layer.name}>{layer.name}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{layer.color.toUpperCase()}</span>
                  </div>
                  <div className="color-input-wrapper">
                    <div className="color-picker-dot" style={{ backgroundColor: layer.color }}>
                      <input 
                        type="color" 
                        value={layer.color} 
                        onChange={(e) => handleColorChange(layer.uuid, e.target.value)} 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
